import React from "react";
import styled from "styled-components";
import { CalendarPlus, CalendarCheck, X, Sparkles, CheckCircle2 } from "lucide-react";

interface AddToCalendarModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  text?: string;
  /** When true, shows an info-only modal that the event is already on the calendar */
  alreadyAdded?: boolean;
}

const AddToCalendarModal: React.FC<AddToCalendarModalProps> = ({
  isOpen,
  onCancel,
  onConfirm,
  text = "Are you sure you want to add this event to your calendar?",
  alreadyAdded = false,
}) => {
  if (!isOpen) return null;

  return (
    <Overlay
      onClick={(e) => {
        e.stopPropagation();
        onCancel();
      }}
      role="presentation"
    >
      <Popup onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <Hero>
          <HeroGlow />
          <HeroTop>
            <HeroIcon $already={alreadyAdded}>
              {alreadyAdded ? <CalendarCheck size={22} /> : <CalendarPlus size={22} />}
            </HeroIcon>
            <HeroText>
              <HeroBadge>
                <Sparkles size={11} />
                Calendar
              </HeroBadge>
              <Title>{alreadyAdded ? "Already in calendar" : "Add to calendar"}</Title>
              <Subtitle>
                {alreadyAdded
                  ? "This event is already added to your calendar. Please check your calendar to view the event details."
                  : text}
              </Subtitle>
            </HeroText>
            <CloseBtn
              type="button"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onCancel();
              }}
              aria-label="Close"
            >
              <X size={18} />
            </CloseBtn>
          </HeroTop>
        </Hero>

        <Body>
          <ActionRow>
            {alreadyAdded ? (
              <ConfirmButton
                type="button"
                $full
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onCancel();
                }}
              >
                Got it
              </ConfirmButton>
            ) : (
              <>
                <CancelButton
                  type="button"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onCancel();
                  }}
                >
                  Cancel
                </CancelButton>
                <ConfirmButton
                  type="button"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onConfirm();
                  }}
                >
                  <CheckCircle2 size={16} />
                  Add to calendar
                </ConfirmButton>
              </>
            )}
          </ActionRow>
        </Body>
      </Popup>
    </Overlay>
  );
};

export default AddToCalendarModal;

const gradient =
  "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  padding: 16px;
  cursor: default;
`;

const Popup = styled.div`
  width: 100%;
  max-width: 440px;
  background: #fff;
  border-radius: 22px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
  cursor: default;
`;

const Hero = styled.div`
  position: relative;
  overflow: hidden;
  padding: 22px 24px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.14) 0%,
    rgba(206, 86, 176, 0.1) 55%,
    rgba(53, 183, 200, 0.06) 100%
  );
  border-bottom: 1px solid rgba(201, 77, 170, 0.1);
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -50px;
  right: -20px;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.25) 0%, transparent 70%);
`;

const HeroTop = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const HeroIcon = styled.div<{ $already?: boolean }>`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) =>
    p.$already
      ? "linear-gradient(144.01deg, rgba(53, 183, 200, 0.9) 0%, rgba(206, 86, 176, 0.85) 100%)"
      : gradient};
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 6px 16px rgba(201, 77, 170, 0.25);
`;

const HeroText = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 4px;
  padding: 3px 9px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: #c94daa;
`;

const Title = styled.h2`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: #171717;
  line-height: 1.2;
`;

const Subtitle = styled.p`
  margin: 6px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  line-height: 1.45;
`;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(23, 23, 23, 0.6);
  flex-shrink: 0;
  transition: all 0.15s ease;
  &:hover {
    color: #c94daa;
    border-color: rgba(201, 77, 170, 0.25);
  }
`;

const Body = styled.div`
  padding: 20px 24px 22px;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 10px;
`;

const CancelButton = styled.button`
  flex: 0 0 auto;
  padding: 0 20px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #fff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.65);
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }
`;

const ConfirmButton = styled.button<{ $full?: boolean }>`
  flex: ${(p) => (p.$full ? "1" : "1")};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(201, 77, 170, 0.25);
  transition: all 0.15s ease;
  &:hover {
    transform: translateY(-1px);
  }
`;
